import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import {
  Rocket, Target, GraduationCap, Trophy, CheckCircle2, ArrowRight, Sparkles,
  Loader2, PartyPopper, ExternalLink, Star, Compass, Eye, Gem, Megaphone, UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import { MBTI_TEST_URL, VIA_TEST_URL, PERSONAL_IDEAL_HELP } from "@/lib/roles";

const STEPS = [
  { icon: Sparkles, title: "Bem-vindo à A4G League",
    desc: "A tua jornada rumo à excelência começa aqui. Vais acumular pontos ao completar objetivos, auto-educação e ao colaborar com a tua equipa.",
    points: ["Ganha pontos por comportamentos de excelência", "Sobe de nível: Iniciante → Excelência", "Desbloqueia badges e reconhecimento"] },
  { icon: Target, title: "Define e conquista objetivos",
    desc: "Objetivos individuais e de equipa reforçam os hábitos certos. Cada conclusão soma pontos ao teu total.",
    points: ["Objetivos individuais para o teu crescimento", "Objetivos de equipa que promovem cooperação", "Recompensas por cada meta atingida"] },
  { icon: GraduationCap, title: "Aprende na Academia",
    desc: "Módulos de auto-educação com artigos e quizzes — alguns gerados por IA — para elevares as tuas competências.",
    points: ["Conteúdos práticos para consultores", "Quizzes que consolidam a aprendizagem", "Pontos por cada módulo concluído"] },
  { icon: Trophy, title: "Cooperação na competição",
    desc: "Sobe no ranking da tua agência. Aqui, competir e cooperar andam de mãos dadas — todos crescem juntos.",
    points: ["Ranking saudável que valoriza o esforço", "Reconhecimento de toda a equipa", "Cultura de entreajuda e excelência"] },
];

const emptyProfile = { mbti: "", mission: "", vision: "", values: "", personal_ideal: "", value_proposition: "" };

function LinkChip({ href, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue hover:text-brand-bluehover">
      {children} <ExternalLink size={12} />
    </a>
  );
}

export default function Onboarding() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [finishing, setFinishing] = useState(false);
  const [profile, setProfile] = useState({ ...emptyProfile, ...(user.profile || {}) });
  const [sponsorId, setSponsorId] = useState("");
  const [members, setMembers] = useState([]);
  const done = user.stats?.badges?.find((b) => b.id === "welcome")?.earned || user.onboarding_completed;

  useEffect(() => {
    api.get("/agency/members").then((r) => setMembers(r.data)).catch(() => {});
  }, []);

  const setP = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const finish = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post("/onboarding/complete", {
        profile, sponsor_id: sponsorId || null,
      });
      if (!data.already) toast.success("Onboarding concluído! +100 pontos 🎉");
      await refresh();
      navigate("/app");
    } catch (e) {
      toast.error("Erro ao concluir onboarding");
    } finally { setFinishing(false); }
  };

  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center py-16" data-testid="onboarding-done">
        <div className="h-16 w-16 rounded-sm bg-gradient-to-br from-brand-blue to-brand-orange flex items-center justify-center mx-auto glow-orange">
          <PartyPopper size={28} className="text-white" />
        </div>
        <h1 className="font-heading font-black text-2xl mt-6">Onboarding concluído!</h1>
        <p className="text-brand-muted mt-2">Já estás pronto para conquistar a A4G League. Podes editar o teu perfil a qualquer momento.</p>
        <div className="flex gap-3 justify-center mt-6">
          <Button onClick={() => navigate("/app")} className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
            Dashboard <ArrowRight size={16} className="ml-1" />
          </Button>
          <Button onClick={() => navigate("/app/perfil")} variant="outline" className="border-brand-border rounded-sm">
            Editar perfil
          </Button>
        </div>
      </div>
    );
  }

  const totalSteps = STEPS.length + 1;
  const isProfileStep = step === STEPS.length;
  const s = STEPS[step];

  return (
    <div className="max-w-2xl mx-auto py-6" data-testid="onboarding-page">
      <div className="flex items-center gap-2 mb-8">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand-orange" : "bg-brand-border"}`} />
        ))}
      </div>

      {!isProfileStep ? (
        <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}
          className="p-8 rounded-sm bg-brand-surface border border-brand-border">
          <div className="h-14 w-14 rounded-sm bg-brand-blue/15 text-brand-blue flex items-center justify-center"><s.icon size={26} /></div>
          <span className="text-xs text-brand-orange font-bold uppercase tracking-wider mt-6 block">Passo {step + 1} de {totalSteps}</span>
          <h1 className="font-heading font-black text-2xl sm:text-3xl mt-2">{s.title}</h1>
          <p className="text-brand-muted mt-3 leading-relaxed">{s.desc}</p>
          <ul className="mt-6 space-y-3">
            {s.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-brand-success mt-0.5 shrink-0" />
                <span className="text-sm text-white/90">{p}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      ) : (
        <motion.div key="profile" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.35 }}
          className="p-8 rounded-sm bg-brand-surface border border-brand-border space-y-5">
          <div>
            <span className="text-xs text-brand-orange font-bold uppercase tracking-wider">Passo {totalSteps} de {totalSteps}</span>
            <h1 className="font-heading font-black text-2xl sm:text-3xl mt-2">O teu perfil</h1>
            <p className="text-brand-muted mt-2">Define quem és como profissional. Poderás editar tudo mais tarde.</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><Star size={14} className="text-brand-orange" /> Personalidade (MBTI)</Label>
              <LinkChip href={MBTI_TEST_URL}>Fazer teste MBTI</LinkChip>
            </div>
            <Input value={profile.mbti} onChange={(e) => setP("mbti", e.target.value)} data-testid="onb-mbti"
              placeholder="Ex: ENFJ" className="mt-1.5 bg-background border-brand-border rounded-sm max-w-[200px]" />
          </div>

          <div>
            <Label className="flex items-center gap-2"><Compass size={14} className="text-brand-blue" /> Missão</Label>
            <Textarea value={profile.mission} onChange={(e) => setP("mission", e.target.value)} data-testid="onb-mission"
              rows={2} placeholder="A minha missão..." className="mt-1.5 bg-background border-brand-border rounded-sm" />
          </div>

          <div>
            <Label className="flex items-center gap-2"><Eye size={14} className="text-brand-blue" /> Visão</Label>
            <Textarea value={profile.vision} onChange={(e) => setP("vision", e.target.value)} data-testid="onb-vision"
              rows={2} placeholder="A minha visão..." className="mt-1.5 bg-background border-brand-border rounded-sm" />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <Label className="flex items-center gap-2"><Gem size={14} className="text-brand-blue" /> Valores</Label>
              <LinkChip href={VIA_TEST_URL}>Descobrir valores (VIA-ME)</LinkChip>
            </div>
            <Textarea value={profile.values} onChange={(e) => setP("values", e.target.value)} data-testid="onb-values"
              rows={2} placeholder="Os meus valores..." className="mt-1.5 bg-background border-brand-border rounded-sm" />
          </div>

          <div className="p-4 rounded-sm bg-brand-blue/5 border border-brand-blue/30">
            <Label className="flex items-center gap-2"><Sparkles size={14} className="text-brand-orange" /> Ideal Pessoal</Label>
            <p className="text-xs text-brand-muted mt-1.5 leading-relaxed">{PERSONAL_IDEAL_HELP}</p>
            <Textarea value={profile.personal_ideal} onChange={(e) => setP("personal_ideal", e.target.value)} data-testid="onb-personal_ideal"
              rows={2} placeholder="A ideia predileta que dá sentido ao meu propósito..." className="mt-2 bg-background border-brand-border rounded-sm" />
          </div>

          <div>
            <Label className="flex items-center gap-2"><Megaphone size={14} className="text-brand-blue" /> Proposta de Valor</Label>
            <Textarea value={profile.value_proposition} onChange={(e) => setP("value_proposition", e.target.value)} data-testid="onb-value_proposition"
              rows={2} placeholder="O que me distingue..." className="mt-1.5 bg-background border-brand-border rounded-sm" />
          </div>

          {members.length > 0 && (
            <div>
              <Label className="flex items-center gap-2"><UserPlus size={14} className="text-brand-orange" /> Quem te apadrinhou?</Label>
              <p className="text-xs text-brand-muted mt-1">O consultor que te integrou ganha pontos de engagement.</p>
              <Select value={sponsorId} onValueChange={setSponsorId}>
                <SelectTrigger className="mt-1.5 bg-background border-brand-border rounded-sm" data-testid="onb-sponsor"><SelectValue placeholder="Selecionar (opcional)" /></SelectTrigger>
                <SelectContent>
                  {members.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </motion.div>
      )}

      <div className="flex justify-between mt-6">
        <Button variant="ghost" onClick={() => setStep((x) => Math.max(0, x - 1))} disabled={step === 0}
          className="text-brand-muted hover:text-white rounded-sm">Anterior</Button>
        {isProfileStep ? (
          <Button onClick={finish} disabled={finishing} data-testid="finish-onboarding-button"
            className="bg-brand-orange hover:bg-brand-orangehover rounded-sm font-bold glow-orange">
            {finishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Concluir e ganhar 100 pts <Rocket size={16} className="ml-1" /></>}
          </Button>
        ) : (
          <Button onClick={() => setStep((x) => x + 1)} data-testid="next-onboarding-button"
            className="bg-brand-blue hover:bg-brand-bluehover rounded-sm font-bold">
            Seguinte <ArrowRight size={16} className="ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
